import type { VesselCallError } from "@features/vessel-call/vessel-call-status/VesselCallStatus.types.tsx";
import type { CompleteResumptionVesselCallByIdArgs } from "@services/vesselCall/types";
import { completeResumptionVesselCallById } from "@services/vesselCallApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

export const useCompleteResumption = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess: (status?: string) => void;
  handleError: (error: VesselCallError) => void;
}) => {
  const completeResumptionMutation = useMutation({
    mutationFn: (variables: Parameters<typeof completeResumptionVesselCallById>[0]) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "VesselCallResumption",
          action: "complete_vessel_call_resumption",
          entityType: "vessel_call",
        },
        (transactionId) =>
          completeResumptionVesselCallById({
            ...variables,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async (_data, variables) => {
      const status = variables?.params?.status as string | undefined;
      handleSuccess(status);
    },
    onError: (error: VesselCallError) => handleError(error),
  });

  const completeResumption = ({
    service,
    vesselCallId,
    params,
  }: {
    service: string;
    vesselCallId: string;
    params: CompleteResumptionVesselCallByIdArgs;
  }) => {
    completeResumptionMutation.mutate({
      service,
      vesselCallId,
      params,
    });
  };

  const completeResumptionAsync = ({
    service,
    vesselCallId,
    params,
  }: {
    service: string;
    vesselCallId: string;
    params: CompleteResumptionVesselCallByIdArgs;
  }) => {
    return completeResumptionMutation.mutateAsync({
      service,
      vesselCallId,
      params,
    });
  };

  return {
    completeResumption,
    completeResumptionAsync,
  };
};
